import { useActionsTrack } from "@/_old/features/analytics";
import { Button } from "@/core/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/core/components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/core/components/ui/tabs";
import { useDashboardFilters } from "@/dashboard/store/useDashboardFilters";
import { Plus } from "lucide-react";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DashboardRankingsCitiesDataTableSheet } from "./cities-data-table-sheet";
import { DashboardRankingsGroupsDataTableSheet } from "./groups-data-table-sheet";
import { DashboardRankingsSpacesDataTableSheet } from "./spaces-data-table-sheet";
import { DashboardRankingsUsersDataTableSheet } from "./users-data-table-sheet";

interface DashboardRankingsSheetProps extends React.ComponentPropsWithoutRef<
  typeof Sheet
> {}

export const DashboardRankingsSheet = ({
  ...props
}: DashboardRankingsSheetProps) => {
  const [translate] = useTranslation(["dashboard"]);
  const { onActionClick, onActionView } = useActionsTrack();
  const { filters } = useDashboardFilters();

  useEffect(() => {
    if (props.open) {
      onActionView("DASHBOARD_RANKINGS_SHEET_VIEW");
    }
  }, [props.open]);

  const handleRankingsTabChange = useCallback(
    (value: string) => {
      switch (value) {
        case "spaces":
          onActionClick("DASHBOARD_RANKINGS_SPACES_TAB_CLICK", "DASHBOARD");
          break;
        case "cities":
          onActionClick("DASHBOARD_RANKINGS_CITIES_TAB_CLICK", "DASHBOARD");
          break;
        case "users":
          onActionClick("DASHBOARD_RANKINGS_USERS_TAB_CLICK", "DASHBOARD");
          break;
        case "groups":
          onActionClick("DASHBOARD_RANKINGS_GROUPS_TAB_CLICK", "DASHBOARD");
          break;
      }
    },
    [onActionClick],
  );

  return (
    <Sheet {...props}>
      <SheetTrigger asChild>
        <Button variant="secondary" size="lg">
          <Plus className="mr-2 w-4 h-4" />{" "}
          {translate("dashboard-rankings-spaces-load-more")}
        </Button>
      </SheetTrigger>

      <SheetContent className="overflow-y-auto !min-w-[1000px]">
        <SheetHeader>
          <SheetTitle>{translate("dashboard-rankings-sheet-title")}</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-4 py-4 dashboard-rankings-sheet--container">
          <Tabs defaultValue="spaces" onValueChange={handleRankingsTabChange}>
            <div className="flex flex-col gap-4">
              <h2 className="text-lg font-medium text-zinc-900">
                {translate("dashboard-rankings-title")}
              </h2>

              <TabsList className="w-full">
                <TabsTrigger value="spaces">
                  {translate("dashboard-rankings-spaces-tab")}
                </TabsTrigger>

                <TabsTrigger value="cities">
                  {translate("dashboard-rankings-cities-tab")}
                </TabsTrigger>

                <TabsTrigger value="users">
                  {translate("dashboard-rankings-users-tab")}
                </TabsTrigger>

                <TabsTrigger value="groups">
                  {translate("dashboard-rankings-groups-tab")}
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="spaces">
              <DashboardRankingsSpacesDataTableSheet filters={filters} />
            </TabsContent>

            <TabsContent value="cities">
              <DashboardRankingsCitiesDataTableSheet filters={filters} />
            </TabsContent>

            <TabsContent value="users">
              <DashboardRankingsUsersDataTableSheet filters={filters} />
            </TabsContent>

            <TabsContent value="groups">
              <DashboardRankingsGroupsDataTableSheet filters={filters} />
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
};
